'use client';

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useReducer,
} from 'react';

import {
  INITIAL_LAYER_STATE,
  type LayerAction,
  type LayerContextValue,
  type LayerNavigationItem,
  type LayerProviderProps,
  type LayerState,
} from './Layer.types';

// =============================================================================
// REDUCER
// =============================================================================

function layerReducer(state: LayerState, action: LayerAction): LayerState {
  switch (action.type) {
    case 'OPEN_LAYER':
      return {
        ...state,
        isOpen: true,
        activeLayer: 1,
        navigationStack: [action.payload],
        transition: 'none',
      };

    case 'CLOSE_LAYER':
      return INITIAL_LAYER_STATE;

    case 'PUSH_CONTENT':
      return {
        ...state,
        navigationStack: [...state.navigationStack, action.payload],
        transition: 'slide-left',
      };

    case 'POP_CONTENT': {
      if (state.navigationStack.length <= 1) {
        return INITIAL_LAYER_STATE;
      }
      return {
        ...state,
        navigationStack: state.navigationStack.slice(0, -1),
        transition: 'slide-right',
      };
    }

    case 'NAVIGATE_TO_INDEX': {
      const targetIndex = action.payload;
      if (targetIndex < 0 || targetIndex >= state.navigationStack.length) {
        return state;
      }
      if (targetIndex === 0 && state.navigationStack[0]?.type === 'plan') {
        return INITIAL_LAYER_STATE;
      }
      const isGoingBack = targetIndex < state.navigationStack.length - 1;
      return {
        ...state,
        navigationStack: state.navigationStack.slice(0, targetIndex + 1),
        transition: isGoingBack ? 'slide-right' : 'none',
      };
    }

    case 'SET_TRANSITION':
      return {
        ...state,
        transition: action.payload,
      };

    default:
      return state;
  }
}

// =============================================================================
// CONTEXT
// =============================================================================

const LayerContext = createContext<LayerContextValue | null>(null);

LayerContext.displayName = 'LayerContext';

// =============================================================================
// PROVIDER
// =============================================================================

export function LayerProvider({ children }: LayerProviderProps) {
  const [state, dispatch] = useReducer(layerReducer, INITIAL_LAYER_STATE);

  const openLayer = useCallback((item: LayerNavigationItem) => {
    dispatch({ type: 'OPEN_LAYER', payload: item });
  }, []);

  const closeLayer = useCallback(() => {
    dispatch({ type: 'CLOSE_LAYER' });
  }, []);

  const pushContent = useCallback((item: LayerNavigationItem) => {
    dispatch({ type: 'PUSH_CONTENT', payload: item });
  }, []);

  const popContent = useCallback(() => {
    dispatch({ type: 'POP_CONTENT' });
  }, []);

  const navigateToIndex = useCallback((index: number) => {
    dispatch({ type: 'NAVIGATE_TO_INDEX', payload: index });
  }, []);

  const value = useMemo<LayerContextValue>(
    () => ({
      state,
      openLayer,
      closeLayer,
      pushContent,
      popContent,
      navigateToIndex,
    }),
    [state, openLayer, closeLayer, pushContent, popContent, navigateToIndex]
  );

  return (
    <LayerContext.Provider value={value}>{children}</LayerContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useLayerContext(): LayerContextValue {
  const context = useContext(LayerContext);
  if (!context) {
    throw new Error('useLayerContext must be used within a LayerProvider');
  }
  return context;
}

export { LayerContext };
